// State
export const state = () => ({
  loading: false,
  gameTypes: [],
  games: { data: [] },
  gameProviders: [],
  isMaintenance: false,
  featuredGames: [],
  latestGameLink: null,
  latestTime: 0,
  timerId: null,
  cancelToken: {},
  lastRequestTime: 0,
  playGameLoading: null,
  redirectPlayGameFullPath: null,
  recommendMatchLoading: false,
  recommendMatchItems: []
})

// Actions
export const actions = {
  // Game types
  async getRecommendedMatchItems({ commit }, params) {
    commit('recommendedMatchLoading', true)
    const language = this.$cookie.get('wl_locale')
    try {
      const response = await this.$axios.get(`games/recommended-matches?language=${language}`)
      commit('recommendedMatches', response.data)
    } catch (error) {}
    finally{
      commit('recommendedMatchLoading', false)
    }
  },

  // Game types
  async gameTypes({ commit }) {
    try {
      const response = await this.$axios.get('/game-types')
      commit('gameTypes', response.data)
    } catch (error) {}
  },

  // Game providers
  async gameProviders({ commit }) {
    try {
      const response = await this.$axios.get('/cms/game-providers')
      commit('gameProviders', response.data)
    } catch (error) {}
  },

  // Games
  async games({ commit }, params) {
    commit('settings/loading', true, { root: true })
    try {
      const response = await this.$axios.get('/games', {
        params
      })
      commit('games', response.data)
    } catch (error) {
    } finally {
      commit('settings/loading', false, { root: true })
    }
  },
  async fetchGameLink({ commit, state }, params) {
    try {
      const response = await this.$axios.get('/game-link', {
        params,
        cancelToken: state.cancelToken.playGame.token
      })

      if (response?.status !== 200) {
        return { message: response.data.message, status: false }
      }

      commit('playGameLoading', null)

      return {
        status: true,
        isSupportIframe: response?.data?.isSupportIframe,
        url: response?.data?.url?.replace('http://', 'https://'),
        message: null
      }
    } catch (e) {
      if (!this.$axios.isCancel(e)) {
        commit('playGameLoading', null)
      }
      return {
        message: e?.response?.data?.message,
        status: false
      }
    }
  },
  // Play
  async playGame({ commit, dispatch, rootState, state }, game) {
    if (!this.$cookie.get('token')) {
      this.$router.push('/login')
      return
    }

    // Cancel any ongoing request
    if (state?.cancelToken?.playGame) {
      state?.cancelToken?.playGame.cancel('')
    }
    commit('setCancelTokens', { playGame: this.$axios.CancelToken.source() })
    commit('settings/loading', true, { root: true })

    const language = rootState['base-settings']?.configuration?.wl_locale
    const params = { ...game, language }

    try {
      let response = { status: false }
      if (state.lastRequestTime) {
        // Check condition
        const nowTime = new Date().getTime()
        const timeDiff = nowTime - state.lastRequestTime
        // Check if timeDiff is more than 3 seconds
        const GAP_TIME = 3100

        if (timeDiff <= GAP_TIME) {
          // 3.09 seconds
          commit('clearTimer')
          // Return a promise that resolves after timeout
          return new Promise(resolve => {
            const _dispatch = dispatch
            const timeOut = setTimeout(async () => {
              const res = await _dispatch('playGame', game)
              resolve(res) // resolve the response here
            }, GAP_TIME - timeDiff)
            commit('setTimerId', timeOut)
          })
        }
      }
      // Set request time
      const nowTime = new Date().getTime()
      commit('setLastRequestTime', nowTime)
      response = await dispatch('fetchGameLink', params)
      return response
    } finally {
      commit('settings/loading', false, { root: true })
    }
  }
}

// Mutations
export const mutations = {
  recommendedMatches(state, data) {
    state.recommendMatchItems = data
  },
  recommendedMatchLoading(state, data){
    state.recommendMatchLoading = data
  },
  gameTypes(state, data) {
    state.gameTypes = data
  },
  gameProviders(state, data) {
    state.gameProviders = data
  },
  games(state, data) {
    state.games = data
  },
  loading(state, type) {
    state.loading = type
  },
  gameImageNotFound(state, game) {
    // state.games.data.find(o => o.id === game.id).gsc_game_image = '/no-image.jpeg'
    const found = state.games.data.find(o => o.id === game.id)
    if (found) {
      found.gsc_game_image = '/no-image.jpeg'
      return found
    }
  },
  providerImageNotFound(state, provider) {
    state.gameProviders.find(o => o.id === provider.id).file_path = 'no-image.jpeg'
  },
  isMaintenance(state, type) {
    state.isMaintenance = type
  },
  featuredGames(state, value) {
    state.featuredGames = value
  },
  setPlayGameLink(state, link) {
    state.latestGameLink = link
  },
  setLatestTime(state, count) {
    state.latestTime = count
  },
  setTimerId(state, id) {
    state.timerId = id
  },
  clearTimer(state) {
    if (state.timerId) {
      clearInterval(state.timerId)
      state.timerId = null
    }
  },
  setCancelTokens: (state, data) => {
    const oldData = { ...state.cancelToken }
    state.cancelToken = {
      ...oldData,
      ...data
    }
  },
  setLastRequestTime: (state, value) => {
    state.lastRequestTime = value
  },
  playGameLoading(state, value) {
    state.playGameLoading = value
  },
  redirectPlayGameFullPath(state, value) {
    state.redirectPlayGameFullPath = value
  }
}

// Getters
export const getters = {
  findProvider: state => providerCode => {
    return state.gameProviders.find(o => o.provider_code === providerCode)
  },
  gameTypes: (state, getters) => providerCode => {
    const provider = getters.findProvider(providerCode)
    if (provider) {
      return state.gameTypes.filter(o => provider.support_game_types.includes(o.code))
    } else {
      return state.gameTypes
    }
  },
  providersWithProductCode: state => productTypeCode => {
    return state.gameProviders.filter(o => o.support_game_types.includes(productTypeCode))
  },
  providersWithFilter: state => (providerCode, productTypeCode) => {
    const filtered = state.gameProviders.filter(o => o.support_game_types.includes(productTypeCode))
    if (providerCode) {
      return filtered.filter(o => o.provider_code === providerCode)
    } else {
      return filtered
    }
  },
  providerHasLobby: (state, getters) => providerCode => {
    const provider = getters.findProvider(providerCode)
    if (provider) {
      return provider.has_lobby
    } else {
      return false
    }
  }
}
